import { CircleAlert, CircleCheck, Info, TriangleAlert } from 'lucide-react';
import type { CSSProperties } from 'react';
import { Toaster as SonnerToaster } from 'sonner';
import { Spinner } from './Spinner';
import { useTheme } from './theme/themeContext';

// Sonner reads these custom properties; pointing them at the tokens themes
// every toast without overriding its layout.
const TOKEN_VARS = {
  '--normal-bg': 'rgb(var(--color-surface-raised))',
  '--normal-border': 'rgb(var(--color-border))',
  '--normal-text': 'rgb(var(--color-fg))',
  '--border-radius': '0.5rem',
} as CSSProperties;

const ICONS = {
  success: <CircleCheck className="size-4 text-success" />,
  error: <CircleAlert className="size-4 text-danger" />,
  warning: <TriangleAlert className="size-4 text-warning" />,
  info: <Info className="size-4 text-accent" />,
  loading: <Spinner size="sm" className="text-fg-muted" />,
};

/**
 * Mount once inside `ThemeProvider`. Trigger toasts with `toast()` from
 * `@/components/ui`, e.g. `toast('Dataset replaced', { action: { label:
 * 'Undo', onClick: undo } })` or `toast.error('Could not read file')`.
 * Toasts are neutral surfaces; only the icon carries the status color.
 */
export const Toaster = () => {
  const { resolvedTheme } = useTheme();
  return (
    <SonnerToaster
      theme={resolvedTheme}
      position="bottom-right"
      closeButton
      icons={ICONS}
      style={TOKEN_VARS}
      toastOptions={{
        classNames: {
          toast: '!items-start !gap-3 !px-4 !py-3 font-sans !shadow-lg',
          title: '!text-sm !font-medium !leading-5 !text-fg',
          description: '!mt-0.5 !text-sm !leading-5 !text-fg-muted',
          icon: '!mt-0.5 !size-4',
          actionButton:
            '!h-7 !self-center !rounded-md !bg-accent !px-2.5 !text-xs !font-medium !text-accent-fg hover:!bg-accent-hover',
          cancelButton:
            '!h-7 !self-center !rounded-md !bg-surface-muted !px-2.5 !text-xs !font-medium !text-fg',
          closeButton:
            '!border-border !bg-surface-raised !text-fg-muted hover:!bg-surface-muted hover:!text-fg',
        },
      }}
    />
  );
};
